import {
  ensurePaymentOperatorSession,
  fetchPaymentGetApi,
  fetchPaymentPatchApi,
  fetchPaymentPostApi,
  PaymentApiErrorResponse,
} from './payment_api';
import { normalizeTossOnboardingStatusResponse, TossOnboardingResponse } from './toss_onboarding';

type TossOnboardingStatusPayload = ReturnType<typeof normalizeTossOnboardingStatusResponse>;
type OperatorId = number | string | undefined;

export type TossOnboardingResetInfo = {
  archivedCid?: number | null;
  archivedShopId?: string | null;
  previousShopId?: string | null;
  reset?: boolean | null;
};

export type TossOnboardingResetResponse = {
  pass?: boolean;
  message?: string | TossOnboardingResetInfo | null;
  code?: string | null;
  errorCode?: string | null;
  error?: string | null;
  orgMessage?: string | null;
  statusCode?: number | null;
};

export type TossOnboardingUpdateModalViewResponse = {
  pass?: boolean;
  message?: string | TossOnboardingResetInfo | null;
  code?: string | null;
  errorCode?: string | null;
  error?: string | null;
  orgMessage?: string | null;
  statusCode?: number | null;
};

export type CreateTossOnboardingRequest = {
  businessRegistrationNumber?: string;
  callbackUrl?: string;
  cardScreeningCallbackUrl?: string;
  forceCreateShop?: boolean;
  returnFallbackUrl?: string;
  returnUrl?: string;
  shopContactEmail?: string;
  shopContactName?: string;
  shopContactPhoneNumber?: string;
  shopName?: string;
  shopUrl?: string;
};

const TOSS_ONBOARDING_STATUS_CACHE_TTL_MS = 5000;
const tossOnboardingStatusRequests = new Map<string, Promise<TossOnboardingStatusPayload>>();
const tossOnboardingStatusCache = new Map<
  string,
  {
    expiresAt: number;
    payload: TossOnboardingStatusPayload;
  }
>();

function normalizeOperatorId(operatorId: OperatorId): number | null {
  const normalizedOperatorId = Number(operatorId);

  return Number.isFinite(normalizedOperatorId) && normalizedOperatorId > 0 ? normalizedOperatorId : null;
}

function getTossOnboardingStatusCacheKey(cid: number | string, operatorId?: OperatorId): string {
  const normalizedOperatorId = normalizeOperatorId(operatorId);

  return normalizedOperatorId === null ? String(cid) : `${cid}:${normalizedOperatorId}`;
}

function clearTossOnboardingStatusCache(cid: number | string, operatorId?: OperatorId): void {
  const cacheKey = getTossOnboardingStatusCacheKey(cid, operatorId);

  tossOnboardingStatusRequests.delete(cacheKey);
  tossOnboardingStatusCache.delete(cacheKey);
}

async function ensureTossOnboardingOperatorSession(operatorId: OperatorId): Promise<PaymentApiErrorResponse | null> {
  if (normalizeOperatorId(operatorId) === null) {
    return null;
  }

  const sessionReady = await ensurePaymentOperatorSession(operatorId);

  return sessionReady
    ? null
    : {
        code: 'PAYMENT_OPERATOR_SESSION_FAILED',
        message: 'Payment operator session failed',
        statusCode: 401,
      };
}

function isPaymentApiErrorResponse(response?: unknown): response is PaymentApiErrorResponse {
  if (typeof response !== 'object' || response === null) {
    return false;
  }

  const statusCode = (response as PaymentApiErrorResponse).statusCode;

  return typeof statusCode === 'number' && statusCode >= 400;
}

function toTossOnboardingResponse(response?: TossOnboardingResponse | PaymentApiErrorResponse | null) {
  if (!isPaymentApiErrorResponse(response)) {
    return response as TossOnboardingResponse;
  }

  const errorCode = response?.code || '';

  return {
    pass: false,
    message: response?.message || errorCode,
    code: errorCode,
    error: errorCode,
    errorCode,
    statusCode: response?.statusCode,
  };
}

function toTossOnboardingResetResponse(
  response?: TossOnboardingResetResponse | PaymentApiErrorResponse | null,
): TossOnboardingResetResponse {
  return toTossOnboardingResponse(
    response as TossOnboardingResponse | PaymentApiErrorResponse | null,
  ) as TossOnboardingResetResponse;
}

export async function fetchTossOnboardingStatus(cid: number | string, operatorId?: OperatorId) {
  const sessionError = await ensureTossOnboardingOperatorSession(operatorId);

  if (sessionError) {
    return normalizeTossOnboardingStatusResponse(toTossOnboardingResponse(sessionError));
  }

  const cacheKey = getTossOnboardingStatusCacheKey(cid, operatorId);
  const cachedStatus = tossOnboardingStatusCache.get(cacheKey);
  const now = Date.now();

  if (cachedStatus && cachedStatus.expiresAt > now) {
    return cachedStatus.payload;
  }

  const pendingStatusRequest = tossOnboardingStatusRequests.get(cacheKey);

  if (pendingStatusRequest) {
    return pendingStatusRequest;
  }

  const statusRequest = fetchPaymentGetApi<TossOnboardingResponse | PaymentApiErrorResponse>(
    `/operator/toss-onboarding/${cid}/status`,
    {
      operatorId,
    },
  )
    .then(response => {
      const payload = normalizeTossOnboardingStatusResponse(toTossOnboardingResponse(response));

      tossOnboardingStatusCache.set(cacheKey, {
        expiresAt: Date.now() + TOSS_ONBOARDING_STATUS_CACHE_TTL_MS,
        payload,
      });

      return payload;
    })
    .finally(() => {
      tossOnboardingStatusRequests.delete(cacheKey);
    });

  tossOnboardingStatusRequests.set(cacheKey, statusRequest);

  return statusRequest;
}

export async function createTossOnboarding(
  cid: number | string,
  body: CreateTossOnboardingRequest,
  operatorId?: OperatorId,
): Promise<TossOnboardingResponse> {
  clearTossOnboardingStatusCache(cid, operatorId);

  const sessionError = await ensureTossOnboardingOperatorSession(operatorId);

  if (sessionError) {
    return toTossOnboardingResponse(sessionError);
  }

  const response = await fetchPaymentPostApi<TossOnboardingResponse | PaymentApiErrorResponse>(
    `/operator/toss-onboarding/${cid}`,
    body,
    {
      operatorId,
    },
  );

  return toTossOnboardingResponse(response);
}

export async function createTossOnboardingApplicationUrl(
  cid: number | string,
  operatorId?: OperatorId,
): Promise<TossOnboardingResponse> {
  clearTossOnboardingStatusCache(cid, operatorId);

  const sessionError = await ensureTossOnboardingOperatorSession(operatorId);

  if (sessionError) {
    return toTossOnboardingResponse(sessionError);
  }

  const response = await fetchPaymentPostApi<TossOnboardingResponse | PaymentApiErrorResponse>(
    `/operator/toss-onboarding/${cid}/application-url`,
    {},
    {
      operatorId,
    },
  );

  return toTossOnboardingResponse(response);
}

export async function resetTossOnboarding(
  cid: number | string,
  operatorId?: OperatorId,
): Promise<TossOnboardingResetResponse> {
  clearTossOnboardingStatusCache(cid, operatorId);

  const sessionError = await ensureTossOnboardingOperatorSession(operatorId);

  if (sessionError) {
    return toTossOnboardingResetResponse(sessionError);
  }

  const response = await fetchPaymentPatchApi<TossOnboardingResetResponse | PaymentApiErrorResponse>(
    `/operator/toss-onboarding/${cid}/reset`,
    {},
    {
      operatorId,
    },
  );

  return toTossOnboardingResetResponse(response);
}

export async function viewedCompleteInfoModal(
  cid: number | string,
  operatorId?: OperatorId,
): Promise<TossOnboardingUpdateModalViewResponse> {
  const response = await fetchPaymentPatchApi<TossOnboardingUpdateModalViewResponse | PaymentApiErrorResponse>(
    `/operator/toss-onboarding/${cid}/modal-view`,
    {},
    {
      operatorId,
    },
  );

  return toTossOnboardingResetResponse(response);
}
